import { useTheme, PALETTES, THEME_LOCKED, type Theme } from '../../hooks/useTheme';

/* ── Commandes de thème ───────────────────────────────────────────────
   Partagées entre l'en-tête de l'application et l'écran de connexion :
   sans elles sur la connexion, impossible d'atteindre le mode pastel (et
   ses nuanciers) avant d'être connecté — le bouton n'existait que dans
   le Header, donc derrière l'authentification. */

// Libellé = le mode SUIVANT dans le cycle (clair → sombre → shader → pastel → clair).
const NEXT_LABEL: Record<Theme, string> = {
  light: 'Mode sombre',
  dark: 'Mode shader',
  shader: 'Mode pastel',
  pastel: 'Mode clair',
};

interface ToggleProps {
  /** Thème et bascule imposés par le parent (Header) ; à défaut, le store partagé. */
  theme?: Theme;
  onToggle?: () => void;
  className?: string;
}

export function ThemeToggleButton({ theme: themeProp, onToggle, className = '' }: ToggleProps) {
  const store = useTheme();
  const theme = themeProp ?? store.theme;
  const handle = onToggle ?? store.toggle;

  // Interface verrouillée sur « Été » : pas de bascule proposée.
  if (THEME_LOCKED) return null;

  return (
    <button
      onClick={handle}
      className={className}
      aria-label={NEXT_LABEL[theme]}
      title={NEXT_LABEL[theme]}
    >
      {theme === 'light' && (
        /* Lune : le mode suivant est le mode sombre */
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
          <path fillRule="evenodd" d="M7.455 2.004a.75.75 0 0 1 .26.77 7 7 0 0 0 9.958 7.967.75.75 0 0 1 1.067.853A8.5 8.5 0 1 1 6.647 1.921a.75.75 0 0 1 .808.083Z" clipRule="evenodd" />
        </svg>
      )}
      {theme === 'dark' && (
        /* Étincelles : le mode suivant est le shader */
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
          <path d="M10 1a1 1 0 0 1 .95.684l1.467 4.4 4.4 1.467a1 1 0 0 1 0 1.898l-4.4 1.467-1.467 4.4a1 1 0 0 1-1.9 0l-1.467-4.4-4.4-1.467a1 1 0 0 1 0-1.898l4.4-1.467 1.467-4.4A1 1 0 0 1 10 1Z" />
          <path d="M16 11a1 1 0 0 1 .894.553l.448.894.894.448a1 1 0 0 1 0 1.21l-.894.448-.448.894a1 1 0 0 1-1.788 0l-.448-.894-.894-.448a1 1 0 0 1 0-1.21l.894-.448.448-.894A1 1 0 0 1 16 11Z" />
        </svg>
      )}
      {theme === 'shader' && (
        /* Palette : le mode suivant est le mode pastel */
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
          <path d="M12 3a9 9 0 0 0 0 18c1.1 0 2-.9 2-2 0-.5-.2-1-.5-1.3-.3-.4-.5-.8-.5-1.2 0-1.1.9-2 2-2h2.3A4.7 4.7 0 0 0 21 9.8C21 6 17 3 12 3Z" />
          <circle cx="7.5" cy="10.5" r="1.2" fill="currentColor" stroke="none" />
          <circle cx="10.5" cy="7" r="1.2" fill="currentColor" stroke="none" />
          <circle cx="15" cy="7.5" r="1.2" fill="currentColor" stroke="none" />
        </svg>
      )}
      {theme === 'pastel' && (
        /* Soleil : le mode suivant est le mode clair */
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
          <path d="M10 2a.75.75 0 0 1 .75.75v1.5a.75.75 0 0 1-1.5 0v-1.5A.75.75 0 0 1 10 2ZM10 15a.75.75 0 0 1 .75.75v1.5a.75.75 0 0 1-1.5 0v-1.5A.75.75 0 0 1 10 15ZM10 7a3 3 0 1 0 0 6 3 3 0 0 0 0-6ZM15.657 5.404a.75.75 0 1 0-1.06-1.06l-1.061 1.06a.75.75 0 0 0 1.06 1.06l1.06-1.06ZM6.464 14.596a.75.75 0 1 0-1.06-1.06l-1.06 1.06a.75.75 0 0 0 1.06 1.06l1.06-1.06ZM18 10a.75.75 0 0 1-.75.75h-1.5a.75.75 0 0 1 0-1.5h1.5A.75.75 0 0 1 18 10ZM5 10a.75.75 0 0 1-.75.75h-1.5a.75.75 0 0 1 0-1.5h1.5A.75.75 0 0 1 5 10ZM14.596 15.657a.75.75 0 0 0 1.06-1.06l-1.06-1.061a.75.75 0 1 0-1.06 1.06l1.06 1.06ZM5.404 6.464a.75.75 0 0 0 1.06-1.06l-1.06-1.06a.75.75 0 1 0-1.06 1.06l1.06 1.06Z" />
        </svg>
      )}
    </button>
  );
}

/* Sélecteur de nuancier + interrupteur sidebar — mode pastel uniquement.
   Chaque pastille porte SON `data-palette` : elle affiche ses propres
   teintes, pas celles du nuancier actif (cf. sélecteur .p-swatch dans
   index.css). Un trait sépare les deux familles (Evinly | Visaly). */
export function PaletteSwatches({ className = '' }: { className?: string }) {
  const { theme, palette, setPalette, effectivePanel, setPanel } = useTheme();
  if (THEME_LOCKED || theme !== 'pastel') return null;

  return (
    <div
      role="radiogroup"
      aria-label="Nuancier du mode pastel"
      className={`items-center gap-1.5 rounded-full bg-white/70 px-2 py-1.5 ${className}`}
    >
      {PALETTES.map((p, i) => (
        <span key={p.key} className="contents">
          {i > 0 && PALETTES[i - 1].family !== p.family && (
            <span aria-hidden="true" className="mx-0.5 h-4 w-px bg-(--p-ink)/20" />
          )}
          <button
            role="radio"
            aria-checked={palette === p.key}
            data-palette={p.key}
            onClick={() => setPalette(p.key)}
            title={`${p.label} — ${p.hint}`}
            className={`p-swatch h-5 w-5 rounded-full transition-transform hover:scale-110 ${
              palette === p.key ? 'ring-2 ring-(--p-ink) ring-offset-2 ring-offset-white' : 'opacity-75 hover:opacity-100'
            }`}
            style={{ background: 'conic-gradient(var(--p-a) 0 25%, var(--p-b) 0 50%, var(--p-c) 0 75%, var(--p-d) 0)' }}
          />
        </span>
      ))}
      {/* Interrupteur sidebar : encre bleu nuit ⇄ bleu Evinly. Chaque nuancier
          a son défaut (famille Evinly = bleu) ; ce bouton le surcharge. */}
      <span aria-hidden="true" className="mx-0.5 h-4 w-px bg-(--p-ink)/20" />
      <button
        onClick={() => setPanel(effectivePanel === 'blue' ? 'ink' : 'blue')}
        aria-pressed={effectivePanel === 'blue'}
        title={effectivePanel === 'blue' ? 'Sidebar bleu Evinly — cliquer pour passer en encre' : 'Sidebar encre — cliquer pour passer en bleu Evinly'}
        className="grid h-5 w-7 place-items-center rounded-full transition-transform hover:scale-110"
      >
        <svg viewBox="0 0 28 20" className="h-4 w-6" aria-hidden="true">
          <rect x="1" y="1" width="26" height="18" rx="5" fill="#fff" stroke="var(--p-ink)" strokeWidth="1.4" />
          <rect x="1" y="1" width="9" height="18" rx="5" fill={effectivePanel === 'blue' ? '#38629f' : 'var(--p-ink)'} />
        </svg>
      </button>
    </div>
  );
}
